import { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Modal, Result, Spin, theme } from "antd";
import { useNavigate } from "react-router-dom";
import { SendOutlined } from "@ant-design/icons";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { CREATE_TASK, GET_INVENTORY_BY_PLACE, GET_QUESTIONS_BY_INVENTORY, SUBMIT_ANSWERS } from "../graphql/queries";
import QuestionGroup from "../components/QuestionGroup";
import AnimatedLogo from "../components/AnimatedLogo";
import IconComponent from "../components/IconComponent";
import { config } from "../config/config";
import { Question, AnswerInput, Place, Role, Inventory } from "../types";
import {
  getAnswerValueCandidates,
  normalizeAnswerCompareValue,
  parseIncidentTaskValues,
  shouldAutoEnableAlarm,
} from "../utils/answerIncident";
import { normalizeAnswerValText } from "../utils/datetimeLocal";
import {
  buildTaskDescriptionFromAnswers,
  findInventoryMeta,
  InventorySubmissionResult,
  logInventoryTagTask,
  pickCheckIdByHighestPriority,
  resolveGuestInitiatorId,
  shouldCreateInventoryTagTask,
  warnInventoryTagTask,
} from "../utils/inventoryTagTask";

interface QuestionPageProps {
  inventory: string[];
  isOneInventory: boolean;
  setInventory: (inventory: string[]) => void;
  deviceId: string;
  placeDetail: Place | null;
  setPlaceDetail: (place: Place | null) => void;
  userRolesdetail?: Role[];
  qrNote?: string | null;
  setQrNote?: (note: string | null) => void;
}

interface Answer {
  [key: string]: any;
}

function getQuestionStateKey(question: Pick<Question, "id" | "inventory_id">): string {
  return `${question.inventory_id || ""}::${question.id}`;
}

function getChatIdsForRole(role: { chat_id?: string | null }): string[] {
  if (!role?.chat_id) return [];
  return role.chat_id
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean);
}

function buildBulkMessages(
  alerts: Array<{ chatIds: string[]; message: string; mediaUrls: string[] }>
): Array<{ chatId: string; mediaUrl: string; message: string }> {
  const messages: Array<{ chatId: string; mediaUrl: string; message: string }> = [];
  for (const alert of alerts) {
    const { chatIds, message, mediaUrls } = alert;
    if (chatIds.length === 0) continue;
    if (mediaUrls.length > 0) {
      chatIds.forEach((chatId) =>
        messages.push({ chatId, mediaUrl: mediaUrls[0], message: message || "" })
      );
      for (let i = 1; i < mediaUrls.length; i++) {
        chatIds.forEach((chatId) =>
          messages.push({ chatId, mediaUrl: mediaUrls[i], message: "" })
        );
      }
    } else {
      chatIds.forEach((chatId) =>
        messages.push({ chatId, mediaUrl: "", message })
      );
    }
  }
  return messages;
}

const GUEST_QUESTION_LAST_SENT_AT_KEY = "guest_question_last_sent_at";

type GuestSentAtMap = Record<string, number>;

function readGuestSentAtMap(): GuestSentAtMap {
  try {
    const raw = localStorage.getItem(GUEST_QUESTION_LAST_SENT_AT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as GuestSentAtMap;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function writeGuestSentAtMap(map: GuestSentAtMap) {
  localStorage.setItem(GUEST_QUESTION_LAST_SENT_AT_KEY, JSON.stringify(map));
}

function parseQuestionIntervalMs(question: Question): number | null {
  const raw = question.interval;
  if (raw == null || raw === "") return null;
  const interval = Number(raw);
  if (!Number.isFinite(interval) || interval <= 0) return null;
  // BE interval is expected in minutes.
  return interval * 60_000;
}

export default function QuestionPage({
  inventory,
  setInventory,
  deviceId,
  placeDetail,
  setPlaceDetail,
  userRolesdetail,
  qrNote,
  setQrNote,
}: QuestionPageProps) {
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const apollo = useApolloClient();
  const [answers, setAnswers] = useState<Answer>({});
  const [modalOpen, setModalOpen] = useState(false);
  const [status, setStatus] = useState<'success' | 'error' | 'required' | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userId = localStorage.getItem("userId") || "";
  const isGuest = !userId;
  const eventId = localStorage.getItem("eventId") || "";
  const successRedirectPath = userId ? "/" : "/thank-you";
  const inventoryIds = Array.isArray(inventory) ? inventory : [];

  const { data, loading } = useQuery<{ getQuestionsByInventory: { questions: Question[]; success: boolean } }>(
    GET_QUESTIONS_BY_INVENTORY,
    {
      variables: { input: { userId: userId || null, eventId, inventoryIds } },
      skip: !eventId || inventoryIds.length === 0,
    }
  );

  const { data: inventoryData } = useQuery<{ getInventoryByPlace: { inventory: Inventory[]; success: boolean } }>(
    GET_INVENTORY_BY_PLACE,
    {
      variables: {
        input: {
          userId: userId || null,
          eventId,
          placeId: placeDetail?.id || "",
        },
      },
      skip: !eventId || !placeDetail?.id,
      fetchPolicy: "cache-and-network",
    }
  );

  const inventoryList = useMemo(
    () => inventoryData?.getInventoryByPlace?.inventory ?? [],
    [inventoryData?.getInventoryByPlace?.inventory]
  );

  const [submitAnswers] = useMutation<{ submitAnswers: { success: boolean; message?: string; checkIds?: string[] } }>(
    SUBMIT_ANSWERS
  );

  const [createTask] = useMutation<{ createTask: { success: boolean; message?: string; taskId: string } }>(
    CREATE_TASK
  );

  const questions = useMemo(
    () => data?.getQuestionsByInventory?.questions ?? [],
    [data?.getQuestionsByInventory?.questions]
  );

  const grouped = useMemo(() => {
    if (!Array.isArray(questions) || questions.length === 0) return null;
    const group: { [key: string]: Question[] } = {};
    for (const q of questions) {
      const key = `${q.inventory_id}|||${q.inventory_name || ''}`;
      if (!group[key]) group[key] = [];
      group[key].push(q);
    }
    return group;
  }, [questions]);

  useEffect(() => {
    if (!inventory || (Array.isArray(inventory) && inventory.length === 0)) {
      if (setPlaceDetail) setPlaceDetail(null);
      navigate("/");
    }
  }, [inventory, navigate, setPlaceDetail]);

  // UI state pro task je přímo z `answers` (switch u otázky)

  const updateAnswer = useCallback((questionStateKey: string, field: string, value: any) => {
    setAnswers((prev) => {
      const nextAnswer = {
        ...prev[questionStateKey],
        [field]: value,
      };
      const question = questions.find((q) => getQuestionStateKey(q) === questionStateKey);
      if (question && shouldAutoEnableAlarm(question, nextAnswer)) {
        nextAnswer.is_alert = true;
      }
      return {
        ...prev,
        [questionStateKey]: nextAnswer,
      };
    });
  }, [questions]);

  const validateMandatory = useCallback((): boolean => {
    for (const q of questions) {
      if (!q.mandatory) continue;
      const answer = answers[getQuestionStateKey(q)];
      // Povinný boolean: bez odpovědi = výchozí zelená (Ano) = považujeme za vyplněno
      if (q.type === "boolean") {
        if (answer && answer.val_boolean === null) return false;
        continue;
      }
      const hasFile = Array.isArray(answer?.val_file)
        ? answer.val_file.length > 0
        : Boolean(answer?.val_file);
      if (!answer || (!answer.val_text && answer.val_number == null && answer.val_real == null && !answer.val_lov && answer.val_boolean == null && !hasFile)) {
        return false;
      }
    }
    return true;
  }, [questions, answers]);

  type SubmitGeo = { latitude: number; longitude: number; placeLocationNames?: string } | null;

  const normalizeValText = useCallback(
    (v: unknown, questionType?: string) => normalizeAnswerValText(v, questionType),
    []
  );

  const getGuestQuestionStorageKey = useCallback(
    (q: Question): string => `${eventId || ""}::${getQuestionStateKey(q)}`,
    [eventId]
  );

  const shouldSendQuestionForGuestInterval = useCallback(
    (q: Question, guestSentAtMap: GuestSentAtMap, nowMs: number): boolean => {
      if (!isGuest) return true;
      const intervalMs = parseQuestionIntervalMs(q);
      if (!intervalMs) return true;
      const storageKey = getGuestQuestionStorageKey(q);
      const lastSentAt = guestSentAtMap[storageKey];
      if (!lastSentAt || !Number.isFinite(lastSentAt)) return true;
      return nowMs - lastSentAt >= intervalMs;
    },
    [isGuest, getGuestQuestionStorageKey]
  );

  const persistGuestLastSentForQuestions = useCallback(
    (sentQuestions: Question[], sentAtMs: number) => {
      if (!isGuest || sentQuestions.length === 0) return;
      const map = readGuestSentAtMap();
      for (const q of sentQuestions) {
        map[getGuestQuestionStorageKey(q)] = sentAtMs;
      }
      writeGuestSentAtMap(map);
    },
    [isGuest, getGuestQuestionStorageKey]
  );

  const buildAnswersPayload = useCallback((targetQuestions: Question[], groupId: string, geo: SubmitGeo = null): AnswerInput[] => {
    return targetQuestions.map((q) => {
      const answer = answers[getQuestionStateKey(q)] || {};
      // Boolean: bez odpovědi = výchozí zelená → odeslat true
      const valBoolean =
        q.type === "boolean"
          ? (answer.val_boolean === false ? false : true)
          : (answer.val_boolean ?? null);
      const base: AnswerInput = {
        questionId: q.id,
        group_id: groupId,
        val_boolean: valBoolean,
        val_text: normalizeValText(answer.val_text, q.type),
        val_number: answer.val_number ?? null,
        val_real: answer.val_real ?? null,
        val_lov: answer.val_lov || null,
        val_file: Array.isArray(answer.val_file) ? answer.val_file.join('|') : (answer.val_file || null),
        is_alert: shouldAutoEnableAlarm(q, answer) ? true : (answer.is_alert ?? null),
        skip_check: answer.skip_check ?? null,
        note: answer.note || null,
      };
      if (geo) {
        base.place_latitude = geo.latitude;
        base.place_longitude = geo.longitude;
        base.place_location_names = geo.placeLocationNames ?? null;
      }
      return base;
    });
  }, [answers]);

  const formatAnswerValue = useCallback((q: Question, answer: Answer): string => {
    const formatDate = (dateString: unknown) => {
      if (!dateString) return "—";
      try {
        const date = new Date(String(dateString));
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
      } catch {
        return "—";
      }
    };
    const formatDateTime = (dateString: unknown) => {
      if (!dateString) return "—";
      try {
        const date = new Date(String(dateString));
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, "0");
        const minutes = String(date.getMinutes()).padStart(2, "0");
        return `${day}.${month}.${year} ${hours}:${minutes}`;
      } catch {
        return "—";
      }
    };
    switch (q.type) {
      case "boolean":
        return answer.val_boolean === true ? "Ano" : "Ne";
      case "text":
        return answer.val_text != null ? String(answer.val_text) : "—";
      case "number":
        return answer.val_number != null ? String(answer.val_number) : "—";
      case "real":
        return answer.val_real != null ? String(answer.val_real) : "—";
      case "lov":
        return answer.val_lov != null ? String(answer.val_lov) : "—";
      case "file":
        return answer.val_file ? "📷 Fotka přiložena" : "—";
      case "date":
        return answer.val_text ? formatDate(answer.val_text) : "—";
      case "datetime":
        return answer.val_text ? formatDateTime(answer.val_text) : "—";
      default:
        return "N/A";
    }
  }, []);

  const parseIncidentList = useCallback(
    (incidentRaw?: string): string[] => parseIncidentTaskValues(incidentRaw),
    []
  );

  const maybeCreateInventoryTagTasks = useCallback(
    async (groupId: string, inventorySubmissions: InventorySubmissionResult[]) => {
      logInventoryTagTask("Start maybeCreateInventoryTagTasks", {
        groupId,
        submissionsCount: inventorySubmissions.length,
        submissions: inventorySubmissions.map((s) => ({
          inventoryId: s.inventoryId,
          inventoryName: s.inventoryName,
          checkIds: s.checkIds,
          questionsCount: s.questions.length,
        })),
        placeDetailId: placeDetail?.id,
        eventId,
        userId: userId || null,
        isGuest,
      });

      if (!inventorySubmissions.length || !groupId) {
        warnInventoryTagTask("Ukončeno – chybí submissions nebo groupId", {
          submissionsCount: inventorySubmissions.length,
          groupId,
        });
        return;
      }

      const resolvedPlaceId =
        placeDetail?.id ||
        inventoryList[0]?.place_id ||
        inventorySubmissions[0]?.questions[0]?.inventory_id ||
        "";

      let list: Inventory[] = [];
      if (resolvedPlaceId && eventId) {
        try {
          logInventoryTagTask("Načítám inventář (network-only)", {
            placeId: resolvedPlaceId,
            eventId,
            userId: userId || null,
          });
          const { data } = await apollo.query<{ getInventoryByPlace: { inventory: Inventory[] } }>({
            query: GET_INVENTORY_BY_PLACE,
            variables: {
              input: {
                userId: userId || null,
                eventId,
                placeId: resolvedPlaceId,
              },
            },
            fetchPolicy: "network-only",
          });
          list = data?.getInventoryByPlace?.inventory ?? [];
          logInventoryTagTask("Inventář načten", {
            count: list.length,
            ids: list.map((i) => i.id),
          });
        } catch (e) {
          console.error("[inventory-tag-task] Chyba načtení inventáře, fallback na cache:", e);
          list = inventoryList;
        }
      } else {
        warnInventoryTagTask("Přeskočen fetch inventáře – použit cache", {
          resolvedPlaceId,
          eventId,
          cacheCount: inventoryList.length,
        });
        list = inventoryList;
      }

      if (!list.length) {
        warnInventoryTagTask("Ukončeno – inventář je prázdný");
        return;
      }

      const placeName = placeDetail?.name || list[0]?.place_name || "—";
      const placeId = resolvedPlaceId || list[0]?.place_id || "";

      for (const submission of inventorySubmissions) {
        if (!shouldCreateInventoryTagTask(list, submission.inventoryId, { log: true })) {
          continue;
        }

        const inventoryMeta = findInventoryMeta(list, submission.inventoryId);
        const checkId = pickCheckIdByHighestPriority(
          submission.questions,
          submission.checkIds,
          (q) => answers[getQuestionStateKey(q)] || {}
        );

        const inventoryName = submission.inventoryName || inventoryMeta?.name || "—";
        const description = buildTaskDescriptionFromAnswers(
          submission.questions,
          (q) => answers[getQuestionStateKey(q)] || {},
          formatAnswerValue
        );

        if (!description.trim()) {
          warnInventoryTagTask("Přeskočeno – prázdný popis tasku", {
            inventoryId: submission.inventoryId,
            questionsCount: submission.questions.length,
          });
          continue;
        }

        const initiatorId = userId || resolveGuestInitiatorId(inventoryMeta?.role_id, userRolesdetail);
        const taskInput = {
          title: `${placeName} - ${inventoryName}`,
          description,
          checkId: checkId || undefined,
          taskGroupId: groupId,
          initiatorId: initiatorId || undefined,
          roleIds: isGuest && inventoryMeta?.role_id ? inventoryMeta.role_id : undefined,
          inventoryId: submission.inventoryId,
          placeId: placeId || inventoryMeta?.place_id,
          eventId: eventId || inventoryMeta?.event_id || undefined,
          status: "in_progress",
        };

        logInventoryTagTask("Volám createTask", taskInput);

        try {
          const result = await createTask({
            variables: { input: taskInput },
          });
          logInventoryTagTask("createTask odpověď", result.data?.createTask);
        } catch (e) {
          console.error("[inventory-tag-task] Chyba při vytváření tasku:", e);
        }
      }

      logInventoryTagTask("maybeCreateInventoryTagTasks dokončeno");
    },
    [
      inventoryList,
      answers,
      formatAnswerValue,
      createTask,
      apollo,
      placeDetail?.name,
      placeDetail?.id,
      userId,
      isGuest,
      userRolesdetail,
      eventId,
    ]
  );

  const maybeCreateIncidentTasks = useCallback(
    async (checkIds?: string[]) => {
      if (!userId) return;
      const checkIdsSafe = Array.isArray(checkIds) ? checkIds : [];
      if (!checkIdsSafe.length) return;

      // Build exactly the same question order as submitAnswersByInventory.
      // checkIds returned by backend follow submitted answers order.
      const submittedQuestionsInOrder: Question[] = grouped
        ? Object.entries(grouped).flatMap(([, groupQuestions]) => groupQuestions)
        : questions;

      const placeName = placeDetail?.name || "—";
      const userName = localStorage.getItem("userName") || "";
      const initiatorId = userId;

      for (let index = 0; index < submittedQuestionsInOrder.length; index += 1) {
        const q = submittedQuestionsInOrder[index];
        const checkId = checkIdsSafe[index];
        if (!checkId) break;

        const ans = answers[getQuestionStateKey(q)] || {};
        if (ans.skip_check) continue;
        const isCompleted = Boolean((ans as any)?.task_completed);
        const candidates = getAnswerValueCandidates(q, ans);
        if (candidates.length === 0) continue;
        const incidentValues = parseIncidentList(q.incident);
        if (incidentValues.length === 0) continue;
        const incidentSet = new Set(incidentValues.map(normalizeAnswerCompareValue));
        const matchedCandidate = candidates.find((c) => incidentSet.has(normalizeAnswerCompareValue(c)));
        if (!matchedCandidate) continue;
        const nowIso = new Date().toISOString();
        const taskStatus = isCompleted ? "completed" : "in_progress";

        try {
          await createTask({
            variables: {
              input: {
                title: `Task: ${placeName} - ${q.question_text}`,
                description: `Hodnota: ${matchedCandidate}${userName ? `\nUživatel: ${userName}` : ""}`,
                checkId,
                initiatorId,
                assigneeId: isCompleted ? initiatorId : undefined,
                priority: q.priority != null ? String(q.priority) : undefined,
                status: taskStatus,
                startTime: isCompleted ? nowIso : undefined,
                endTime: isCompleted ? nowIso : undefined,
              },
            },
          });
        } catch (e) {
          console.error("Chyba při vytváření tasku:", e);
        }
      }
    },
    [
      grouped,
      questions,
      answers,
      createTask,
      parseIncidentList,
      placeDetail?.name,
      userId,
    ]
  );

  const sendAlertsToWhatsapp = useCallback(
    async (qs: Question[], ans: Answer) => {
      const alertsData: Array<{ chatIds: string[]; message: string; mediaUrls: string[] }> = [];
      const userName = localStorage.getItem("userName") || "";
      const placeName = placeDetail?.name || "—";

      // Fallback: если у роли вопроса нет chat_id, берём первый доступный из любых ролей
      const fallbackChatIds =
        userRolesdetail?.map((r) => getChatIdsForRole(r)).find((ids) => ids.length > 0) ?? [];

      for (const q of qs) {
        const answer = ans[getQuestionStateKey(q)] || {};
        if (!answer.is_alert || answer.skip_check) continue;

        const role = userRolesdetail?.find((r) => r.id === q.role_id);
        let chatIds = getChatIdsForRole(role || {});
        if (chatIds.length === 0) chatIds = fallbackChatIds;
        if (chatIds.length === 0) continue;

        const valueLovForBoolean =
          q.type === "boolean" && answer.val_lov
            ? `Důvod: ${answer.val_lov}\n`
            : "";
        const popis =
          answer.note && q.type !== "text" ? `Popis: ${answer.note}` : "";
        const userNameLine = userName ? `\nUživatel: ${userName} ` : "";

        const message =
          `ALARM\nMísto: ${placeName}${userNameLine}\n` +
          `Inventář: ${q.inventory_name || "—"}\n` +
          `Otázka: ${q.question_text}\n` +
          `Odpověď: ${formatAnswerValue(q, answer)}\n` +
          `${valueLovForBoolean}${popis}`;

        let mediaUrls: string[] = [];
        if (typeof answer.val_file === "string" && answer.val_file.includes("|")) {
          mediaUrls = answer.val_file.split("|").filter(Boolean);
        } else if (Array.isArray(answer.val_file)) {
          mediaUrls = answer.val_file.filter(Boolean);
        } else if (answer.val_file) {
          mediaUrls = [String(answer.val_file)];
        }

        alertsData.push({ chatIds, message, mediaUrls });
      }

      const messages = buildBulkMessages(alertsData);
      if (messages.length === 0) return;

      try {
        await fetch(`${config.whatsappApiUrl}/send-media-bulk`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${config.whatsappApiToken}`,
          },
          body: JSON.stringify({ messages }),
        });
      } catch (err) {
        console.error("❌ Chyba při odesílání alertů na WhatsApp:", err);
      }
    },
    [placeDetail?.name, userRolesdetail, formatAnswerValue]
  );

  const submitAnswersByInventory = useCallback(
    async (
      geo: SubmitGeo,
      personLatitude: number | null,
      personLongitude: number | null
    ): Promise<{
      success: boolean;
      checkIds: string[];
      sentQuestions: Question[];
      sentAtMs: number;
      groupId: string;
      inventorySubmissions: InventorySubmissionResult[];
    }> => {
      if (!grouped) {
        return {
          success: false,
          checkIds: [],
          sentQuestions: [],
          sentAtMs: Date.now(),
          groupId: "",
          inventorySubmissions: [],
        };
      }

      const allCheckIds: string[] = [];
      const sentQuestions: Question[] = [];
      const inventorySubmissions: InventorySubmissionResult[] = [];
      const submissionGroupId = crypto.randomUUID();
      const nowMs = Date.now();
      const guestSentAtMap = readGuestSentAtMap();
      for (const [key, groupQuestions] of Object.entries(grouped)) {
        const [inventoryId, inventoryName = ""] = key.split("|||");
        const questionsToSend = groupQuestions.filter((q) =>
          shouldSendQuestionForGuestInterval(q, guestSentAtMap, nowMs)
        );
        const answersPayload = buildAnswersPayload(questionsToSend, submissionGroupId, geo);

        if (answersPayload.length === 0) continue;

        const { data: result } = await submitAnswers({
          variables: {
            input: {
              eventId,
              placeId: placeDetail?.id || "",
              inventoryId,
              userId: userId || null,
              deviceNumber: deviceId,
              latitude: personLatitude,
              longitude: personLongitude,
              answers: answersPayload,
              qrNote: qrNote ?? null,
            },
          },
        });

        if (!result?.submitAnswers?.success) {
          return {
            success: false,
            checkIds: allCheckIds,
            sentQuestions,
            sentAtMs: nowMs,
            groupId: submissionGroupId,
            inventorySubmissions,
          };
        }

        const batchCheckIds = Array.isArray(result.submitAnswers.checkIds)
          ? result.submitAnswers.checkIds
          : [];

        if (batchCheckIds.length > 0) {
          allCheckIds.push(...batchCheckIds);
        }

        inventorySubmissions.push({
          inventoryId,
          inventoryName,
          checkIds: batchCheckIds,
          questions: questionsToSend,
        });
        logInventoryTagTask("submitAnswers batch hotov", {
          inventoryId,
          inventoryName,
          checkIds: batchCheckIds,
          questionsCount: questionsToSend.length,
          groupId: submissionGroupId,
        });
        sentQuestions.push(...questionsToSend);
      }

      return {
        success: true,
        checkIds: allCheckIds,
        sentQuestions,
        sentAtMs: nowMs,
        groupId: submissionGroupId,
        inventorySubmissions,
      };
    },
    [
      grouped,
      buildAnswersPayload,
      submitAnswers,
      eventId,
      placeDetail?.id,
      userId,
      deviceId,
      shouldSendQuestionForGuestInterval,
      qrNote,
    ]
  );

  const buildPlaceLocationNames = useCallback((): string => {
    const loc = placeDetail?.loc_name?.trim() || "";
    const place = placeDetail?.name?.trim() || "";
    if (loc && place) return `${loc} / ${place}`;
    return loc || place;
  }, [placeDetail?.loc_name, placeDetail?.name]);

  const handleSubmit = useCallback(async () => {
    if (!validateMandatory()) {
      setStatus("required");
      setModalOpen(true);
      return;
    }

    setStatus(null);
    setIsSubmitting(true);
    navigator.geolocation.getCurrentPosition(
      async ({ coords }) => {
        try {
          // Pozice stánku (místa) z API – do každé odpovědi place_latitude/place_longitude
          const placeGeo =
            placeDetail?.geo_location &&
            placeDetail.geo_location.latitude != null &&
            placeDetail.geo_location.longitude != null
              ? {
                  latitude: placeDetail.geo_location.latitude,
                  longitude: placeDetail.geo_location.longitude,
                  placeLocationNames: buildPlaceLocationNames(),
                }
              : null;
          const result = await submitAnswersByInventory(
            placeGeo,
            coords.latitude,
            coords.longitude
          );
          // latitude/longitude výše = pozice člověka (zařízení), place_* v answers = pozice stánku

          if (result.success) {
            logInventoryTagTask("submitAnswers úspěch, spouštím vytvoření tasků", {
              groupId: result.groupId,
              checkIds: result.checkIds,
              inventorySubmissions: result.inventorySubmissions,
            });
            await maybeCreateInventoryTagTasks(result.groupId, result.inventorySubmissions);
            await maybeCreateIncidentTasks(result.checkIds);
            try {
              await sendAlertsToWhatsapp(questions, answers);
            } catch (alertErr) {
              console.error("Chyba při odesílání alertů:", alertErr);
            }
            persistGuestLastSentForQuestions(result.sentQuestions, result.sentAtMs);
            setQrNote?.(null);
            setStatus("success");
            setModalOpen(true);
            setIsSubmitting(false);
            setTimeout(() => {
              if (setPlaceDetail) setPlaceDetail(null);
              navigate(successRedirectPath);
            }, 2000);
          } else {
            setStatus("error");
            setModalOpen(true);
            setIsSubmitting(false);
          }
        } catch (err) {
          console.error("Chyba při odesílání odpovědi:", err);
          setStatus("error");
          setModalOpen(true);
          setIsSubmitting(false);
        }
      },
      async (err) => {
        console.log("Chyba při získání polohy:", err?.message);
        try {
          const placeGeo =
            placeDetail?.geo_location &&
            placeDetail.geo_location.latitude != null &&
            placeDetail.geo_location.longitude != null
              ? {
                  latitude: placeDetail.geo_location.latitude,
                  longitude: placeDetail.geo_location.longitude,
                  placeLocationNames: buildPlaceLocationNames(),
                }
              : null;
          const result = await submitAnswersByInventory(placeGeo, null, null);

          if (result.success) {
            logInventoryTagTask("submitAnswers úspěch, spouštím vytvoření tasků", {
              groupId: result.groupId,
              checkIds: result.checkIds,
              inventorySubmissions: result.inventorySubmissions,
            });
            await maybeCreateInventoryTagTasks(result.groupId, result.inventorySubmissions);
            await maybeCreateIncidentTasks(result.checkIds);
            try {
              await sendAlertsToWhatsapp(questions, answers);
            } catch (alertErr) {
              console.error("Chyba při odesílání alertů:", alertErr);
            }
            persistGuestLastSentForQuestions(result.sentQuestions, result.sentAtMs);
            setQrNote?.(null);
            setStatus("success");
            setModalOpen(true);
            setIsSubmitting(false);
            setTimeout(() => {
              if (setPlaceDetail) setPlaceDetail(null);
              navigate(successRedirectPath);
            }, 2000);
          } else {
            setStatus("error");
            setModalOpen(true);
            setIsSubmitting(false);
          }
        } catch (error) {
          setStatus("error");
          setModalOpen(true);
          setIsSubmitting(false);
        }
      }
    );
  }, [validateMandatory, submitAnswersByInventory, sendAlertsToWhatsapp, maybeCreateIncidentTasks, maybeCreateInventoryTagTasks, persistGuestLastSentForQuestions, questions, answers, placeDetail, navigate, setPlaceDetail, successRedirectPath, setQrNote, buildPlaceLocationNames]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <AnimatedLogo loading="spinner" />
      </div>
    );
  }

  if (!grouped || Object.keys(grouped).length === 0) {
    return (
      <div style={{ padding: 16 }}>
        <Result
          title={isGuest ? "Toto místo bohužel neobsahuje žádná kritéria k hodnocení." : "Žádné otázky"}
          subTitle={isGuest ? undefined : "Pro tento inventář nejsou k dispozici žádné otázky."}
        />
      </div>
    );
  }

  return (
    <div style={{ padding: 16 }}>
      <Spin spinning={isSubmitting} delay={500}>
        <div
          style={{
            pointerEvents: isSubmitting ? "none" : undefined,
            opacity: isSubmitting ? 0.75 : 1,
            transition: "opacity 0.2s ease",
          }}
        >
          {Object.entries(grouped).map(([key, groupQuestions]) => {
            const [inventoryId, inventoryName] = key.split("|||");
            const groupQuestionsSorted = [...groupQuestions].sort(
              (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
            );
            const inventoryIcon = groupQuestionsSorted[0]?.inventory_icon;
            return (
              <section
                key={inventoryId || key}
                style={{ marginBottom: 24 }}
              >
                <div
                  className="inventory-sticky-header"
                  style={{ ["--inventory-sticky-border-color" as any]: token.colorBorderSecondary }}
                >
                  {inventoryIcon ? <IconComponent icon={inventoryIcon} size={22} /> : null}
                  <span style={{ fontWeight: 800, fontSize: 20, lineHeight: 1.2 }}>
                    {inventoryName || "Inventář"}
                  </span>
                </div>
                <QuestionGroup
                  questions={groupQuestionsSorted}
                  answers={answers}
                  onChange={updateAnswer}
                />
              </section>
            );
          })}

          <Button
            type="primary"
            block
            loading={isSubmitting}
            disabled={isSubmitting}
            onClick={handleSubmit}
            style={{
              marginTop: 24,
              height: 64,
              fontSize: 24,
              fontWeight: "bold",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 12,
            }}
          >
            {isSubmitting ? "Odesílám…" : "Odeslat"} <SendOutlined style={{ fontSize: 20 }} />
          </Button>
        </div>
      </Spin>

      <Modal
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        closable={false}
      >
        {status === "success" && (
          <Result
            status="success"
            title="Odpovědi byly úspěšně odeslány"
            subTitle="Budete přesměrováni na hlavní stránku..."
          />
        )}
        {status === "error" && (
          <Result
            status="error"
            title="Chyba při odesílání"
            subTitle="Zkuste to prosím znovu."
            extra={
              <Button type="primary" onClick={() => setModalOpen(false)}>
                OK
              </Button>
            }
          />
        )}
        {status === "required" && (
          <Result
            status="warning"
            title="Vyplňte povinná pole"
            subTitle="Prosím vyplňte všechna povinná pole před odesláním."
            extra={
              <Button type="primary" onClick={() => setModalOpen(false)}>
                OK
              </Button>
            }
          />
        )}
      </Modal>
    </div>
  );
}
